import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BookOpen,
  ChevronDown,
  ClipboardList,
  Cpu,
  Filter,
  GraduationCap,
  Hospital,
  Info,
  Layers,
  Lightbulb,
  ListChecks,
  LogOut,
  Menu,
  Moon,
  Network,
  Palette,
  Printer,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Trophy,
  UserCog,
  X,
  type LucideIcon,
} from "lucide-react";
import { getProfile } from "../lib/queries";
import { Breadcrumb } from "../components/Breadcrumb";
import { JourneyPanel } from "../components/JourneyPanel";
import { VisitTracker } from "../components/VisitTracker";
import { ScrollToTop } from "../components/ScrollToTop";
import { useInitSettings, useTheme, setTheme, markFeatureTried } from "../lib/settings";
import { getView } from "../lib/viewState";
import { AskDock } from "../components/ChatPanel";
import { FeedbackDialog } from "../components/Feedback";
import { BugReviewMode } from "../components/BugReviewMode";
import { DisclaimerDialog, openDisclaimer } from "../components/Disclaimer";
import { useConfigHasDraft } from "../components/ConfigManagement";
import { useAppConfig } from "../lib/appConfig";
import { RegisterDialog } from "../components/Register";
import { AuthMenu } from "../auth/AuthMenu";
import { useAuth } from "../auth/useAuth";
import { authEnabled } from "../auth/config";

const CAREER_DEFS = [
  { key: "eras", label: "Eras", Icon: Activity },
  { key: "timeline", label: "Timeline", Icon: Layers },
  { key: "graph", label: "Graph", Icon: Network },
] as const;

const LENS_DEFS = [
  { key: "industry", label: "Industry", Icon: Hospital },
  { key: "skill", label: "Skill", Icon: Lightbulb },
  { key: "role", label: "Role", Icon: Trophy },
  { key: "technology", label: "Technology", Icon: Cpu },
] as const;

interface DropdownItem {
  key: string;
  label?: string;
  Icon?: LucideIcon;
  active?: boolean;
  onClick?: () => void;
  separator?: boolean;
}

function Dropdown({
  label,
  Icon: TriggerIcon,
  triggerClass = "",
  items,
}: {
  label: ReactNode;
  Icon: LucideIcon;
  triggerClass?: string;
  items: DropdownItem[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeT.current) {
      clearTimeout(closeT.current);
      closeT.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeT.current = setTimeout(() => setOpen(false), 150);
  };
  useEffect(() => cancelClose, []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div
      className="dd"
      ref={ref}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className={`dd-trigger ${triggerClass} ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <TriggerIcon size={15} />
        <span>{label}</span>
        <ChevronDown size={14} className="dd-caret" />
      </button>
      {open && (
        <div className="dd-menu" role="menu">
          {items.map((it) =>
            it.separator ? (
              <div key={it.key} className="dd-sep-line" role="separator" />
            ) : (
              <button
                key={it.key}
                type="button"
                className={`dd-item ${it.active ? "active" : ""}`}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
              >
                {it.Icon && <it.Icon size={16} className="dd-item-ico" />}
                <span className="dd-item-label">{it.label}</span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// Scroll the section under the 64px sticky topbar.
function scrollToSection(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (el) {
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 64,
      behavior: "smooth",
    });
  }
}

function Topbar() {
  const profile = getProfile();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  // Mobile: the nav collapses behind a burger. Close it whenever the route changes (any nav item
  // navigates) and on Escape, so it never lingers over the page.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    // Closing the drawer is exactly the "sync to a route change" case this effect is for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileOpen(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const goView = (param: "cview" | "lview", key: string, sectionId: string) => () => {
    const params = new URLSearchParams(location.search);
    params.set(param, key);
    const search = `?${params.toString()}`;
    if (!onHome || location.search !== search) navigate({ pathname: "/", search });
    const doScroll = () => scrollToSection(sectionId);
    if (onHome) {
      doScroll();
      requestAnimationFrame(doScroll);
    } else {
      setTimeout(doScroll, 440);
    }
  };

  const goCredentials = () => {
    if (!onHome) navigate("/");
    const doScroll = () => scrollToSection("credentials");
    if (onHome) {
      doScroll();
      requestAnimationFrame(doScroll);
    } else {
      setTimeout(doScroll, 440);
    }
  };

  // Each dropdown reflects the current view/lens, held across pages: explicit URL param wins, else
  // the persisted choice, else the default. (Was home-only, so the selection looked lost off-home.)
  const query = new URLSearchParams(location.search);
  const cviewKey = query.get("cview") || getView("cview") || "eras";
  const lviewKey = query.get("lview") || getView("lview") || "industry";
  const careerItems: DropdownItem[] = CAREER_DEFS.map((d) => ({
    ...d,
    active: cviewKey === d.key,
    onClick: goView("cview", d.key, "career-views"),
  }));
  const lensItems: DropdownItem[] = LENS_DEFS.map((d) => ({
    ...d,
    active: lviewKey === d.key,
    onClick: goView("lview", d.key, "lens-views"),
  }));
  const cfg = useAppConfig();
  const aboutItems: DropdownItem[] = [];
  if (cfg.header.caseStudy) {
    aboutItems.push({
      key: "app",
      label: "Interactive Resume App",
      Icon: Sparkles,
      active: location.pathname === "/app",
      onClick: () => navigate("/app"),
    });
  }
  if (cfg.header.designSystem) {
    aboutItems.push({
      key: "design",
      label: "Design system",
      Icon: Palette,
      active: location.pathname === "/design-system",
      onClick: () => navigate("/design-system"),
    });
  }
  aboutItems.push({
    key: "disclaimer",
    label: "Disclaimer",
    Icon: ShieldCheck,
    active: false,
    onClick: () => openDisclaimer(),
  });
  const cvSel = careerItems.find((i) => i.active);
  const lvSel = lensItems.find((i) => i.active);
  const ddLabel = (base: string, sel: DropdownItem | undefined, cls?: string): ReactNode =>
    sel ? (
      <>
        {base}
        <span className={"dd-sel" + (cls ? " " + cls : "")}>{sel.label}</span>
      </>
    ) : (
      base
    );

  return (
    <header className="topbar print:hidden" data-c="Topbar">
      <div className="wrap topbar-inner">
        <Link to="/" className="wordmark" onClick={() => setMobileOpen(false)}>
          <img className="mark" src="/favicon.svg" alt="" width={36} height={36} />
          <span className="wordmark-text">
            <span className="eyebrow">Interactive Résumé</span>
            <span className="name">{profile.name}</span>
          </span>
        </Link>
        <button
          type="button"
          className="nav-burger icon-btn"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="primary-nav"
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <nav id="primary-nav" className={`nav ${mobileOpen ? "nav--open" : ""}`}>
          <Dropdown label="About" Icon={Info} triggerClass="nav-link" items={aboutItems} />
          <Dropdown
            label={ddLabel("Views", cvSel)}
            Icon={Activity}
            triggerClass="nav-link"
            items={careerItems}
          />
          <Dropdown
            label={ddLabel("Lens", lvSel, "dd-sel-lens")}
            Icon={Filter}
            triggerClass="nav-link"
            items={lensItems}
          />
          <span className="nav-div" />
          <button type="button" className="nav-link" onClick={goCredentials}>
            <GraduationCap size={15} />
            Credentials
          </button>
          <NavLink
            to="/print"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <Printer size={15} />
            Print
          </NavLink>
          {authEnabled && <AdminMenu />}
          <span className="nav-div" />
          <button
            className="icon-btn"
            onClick={() => {
              setTheme(theme === "dark" ? "light" : "dark");
              markFeatureTried("theme");
            }}
            title="Toggle theme"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          {authEnabled && <AuthMenu />}
        </nav>
      </div>
    </header>
  );
}

// Admin-only topbar link to the architecture view. Mounted behind `authEnabled` so useAuth() is safe.
// Admin-only dropdown: the single entry point to the admin tools — Documentation (which holds the
// READMEs + Architecture / Case Study / Design System) and the Configuration Management modals
// (App Settings, Admin Profile).
function AdminMenu() {
  const { isAdmin, logout } = useAuth();
  const hasDraft = useConfigHasDraft();
  const navigate = useNavigate();
  const location = useLocation();
  if (!isAdmin) return null;
  const items: DropdownItem[] = [
    {
      key: "profile",
      label: "Admin Profile",
      Icon: UserCog,
      active: location.pathname === "/admin/profile",
      onClick: () => navigate("/admin/profile"),
    },
    {
      key: "content",
      label: "Content review",
      Icon: ListChecks,
      active: location.pathname === "/admin/content",
      onClick: () => navigate("/admin/content"),
    },
    {
      key: "settings",
      label: "General Settings",
      Icon: Settings,
      active: location.pathname === "/admin/settings",
      onClick: () => navigate("/admin/settings"),
    },
    {
      key: "models",
      label: "Model settings",
      Icon: Cpu,
      active: location.pathname === "/admin/models",
      onClick: () => navigate("/admin/models"),
    },
    {
      key: "tracker",
      label: "Tracker",
      Icon: ClipboardList,
      active: location.pathname === "/admin/tracker",
      onClick: () => navigate("/admin/tracker"),
    },
    { key: "sep1", separator: true },
    {
      key: "docs",
      label: "Documentation",
      Icon: BookOpen,
      active: location.pathname === "/admin/docs",
      onClick: () => navigate("/admin/docs"),
    },
    { key: "sep2", separator: true },
    {
      key: "signout",
      label: "Sign out",
      Icon: LogOut,
      active: false,
      onClick: () => logout(),
    },
  ];
  return (
    <Dropdown
      label={
        hasDraft ? (
          <>
            Admin <span className="cfg-draft-dot" aria-label="unsaved config draft" />
          </>
        ) : (
          "Admin"
        )
      }
      Icon={ShieldCheck}
      triggerClass="nav-link"
      items={items}
    />
  );
}

export function Base() {
  useInitSettings();
  const profile = getProfile();
  const cfg = useAppConfig();

  const fp = cfg.profile;
  const fullName = `${fp.firstName} ${fp.lastName}`.trim() || profile.name;
  const firstName = fp.firstName.trim() || profile.name.split(" ")[0] || profile.name;
  const year =
    cfg.footer.copyrightYear === "auto" ? new Date().getFullYear() : cfg.footer.copyrightYear;
  const footerTitle =
    cfg.footer.titleFormat === "app"
      ? cfg.appTitle
      : cfg.footer.titleFormat === "name-possessive-app"
        ? `${firstName}'s ${cfg.appTitle}`
        : `${fullName} - ${cfg.appTitle}`;
  const show = cfg.footer.show;
  const linkedin = fp.social.linkedin || profile.linkedin;
  const email = fp.contactEmail || profile.email;

  // Keep the browser tab title in sync with the configured app title.
  useEffect(() => {
    document.title = footerTitle;
  }, [footerTitle]);

  return (
    <div className="min-h-dvh flex flex-col" style={{ color: "var(--ink)" }}>
      <Topbar />
      <Breadcrumb />
      <VisitTracker />
      <ScrollToTop />
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="footer print:hidden" data-c="Footer">
        <div className="wrap between" style={{ flexWrap: "wrap", gap: 12 }}>
          <span className="row">
            <span>© {year} · {footerTitle}</span>
          </span>
          <span className="row" style={{ flexWrap: "wrap", gap: 20 }}>
            {show.aiReadable && (
              <a href="/corpus/resume.md" className="hero-meta-link" target="_blank" rel="noopener">
                AI-readable résumé
              </a>
            )}
            {show.linkedin && linkedin && (
              <a href={linkedin} className="hero-meta-link" target="_blank" rel="noopener">
                LinkedIn
              </a>
            )}
            {show.facebook && fp.social.facebook && (
              <a href={fp.social.facebook} className="hero-meta-link" target="_blank" rel="noopener">
                Facebook
              </a>
            )}
            {show.instagram && fp.social.instagram && (
              <a href={fp.social.instagram} className="hero-meta-link" target="_blank" rel="noopener">
                Instagram
              </a>
            )}
            {show.contactEmail && email && (
              <a href={`mailto:${email}`} className="hero-meta-link">
                {email}
              </a>
            )}
            {show.contactPhone && fp.contactPhone && (
              <a href={`tel:${fp.contactPhone}`} className="hero-meta-link">
                {fp.contactPhone}
              </a>
            )}
          </span>
        </div>
        <div className="wrap between" style={{ flexWrap: "wrap", gap: 12 }}>
          <span className="row">
            <span className="footer-note-label mono">Disclaimer</span>
            <span className="footer-note">
              <span className="row">{cfg.footer.disclaimerText}</span>
            </span>
          </span>
        </div>
      </footer>
      <AskDock />
      <JourneyPanel />
      <FeedbackDialog />
      <BugReviewMode />
      <DisclaimerDialog />
      <RegisterDialog />
    </div>
  );
}
